// The username dialog (protocol §3.2, @ballio/shared account.ts): asked once, right after the
// first sign-in (skippable: "Later") and again before the first money game (then required).
// Unique ignoring case, 3–15 of [A-Za-z0-9_], set once; the field checks the rule as you type,
// the server decides uniqueness (409 taken) and refuses a second set (409 alreadySet).
//
// `usernameFormView` is DOM-free (unit-tested). Consumer: app.ts (`askUsername`).

import { usernameProblem, USERNAME_MAX, type UsernameProblem } from '@ballio/shared';
import type { SetUsernameResult } from '../../net/account-api.js';
import { h } from '../dom.js';
import { type I18nKey, t } from '../i18n/t.js';
import { type ModalHandle, openModal } from './modal.js';

/** Why the last Save failed (server side), or null. */
export type UsernameServerError = 'taken' | 'alreadySet' | 'failed' | null;

export interface UsernameFormInput {
  readonly value: string;
  /** The field was edited (rule hints show only then; an empty untouched field is quiet). */
  readonly touched: boolean;
  readonly serverError: UsernameServerError;
  readonly busy: boolean;
}

export interface UsernameFormView {
  /** The line under the field: the rule, a rule problem or the server's refusal. */
  readonly hint: string;
  /** The hint is an error (red). */
  readonly hintIsError: boolean;
  readonly canSubmit: boolean;
  readonly saveLabel: string;
}

export function usernameProblemText(p: UsernameProblem): string {
  return t(`username.problem.${p}` as I18nKey);
}

/** What the dialog shows for the field's state (DOM-free). */
export function usernameFormView(m: UsernameFormInput): UsernameFormView {
  const name = m.value.trim();
  const problem = usernameProblem(name);
  const saveLabel = m.busy ? t('username.saving') : t('username.save');
  if (m.serverError !== null && !m.busy) {
    const key =
      m.serverError === 'taken'
        ? 'username.taken'
        : m.serverError === 'alreadySet'
          ? 'username.alreadySet'
          : 'username.failed';
    return { hint: t(key), hintIsError: true, canSubmit: problem === null, saveLabel };
  }
  if (problem !== null && m.touched && name !== '') {
    return { hint: usernameProblemText(problem), hintIsError: true, canSubmit: false, saveLabel };
  }
  return {
    hint: t('username.rules'),
    hintIsError: false,
    canSubmit: problem === null && !m.busy,
    saveLabel,
  };
}

/** What Save did: set (the name), or keep the dialog open with the server's reason. */
export function usernameSubmitOutcome(
  r: SetUsernameResult | null,
):
  | { readonly done: true; readonly name: string | null }
  | { readonly done: false; readonly error: Exclude<UsernameServerError, null> } {
  if (r === null) return { done: false, error: 'failed' };
  if (r.status === 'ok') return { done: true, name: r.account.username };
  switch (r.error) {
    case 'taken':
      return { done: false, error: 'taken' };
    case 'alreadySet':
      // Set elsewhere (another tab): the account now has one, so the dialog is done.
      return { done: true, name: null };
    default:
      return { done: false, error: 'failed' };
  }
}

export interface UsernameDialogDeps {
  submit(name: string): Promise<SetUsernameResult | null>;
  /** "Later" is offered (first sign-in); false before a money game. */
  readonly skippable: boolean;
  playClick?(): void;
}

/**
 * Opens the dialog. Resolves the saved name (or null when the account already had one set
 * elsewhere), or `undefined` when closed without saving.
 */
export function openUsernameDialog(deps: UsernameDialogDeps): Promise<string | null | undefined> {
  return new Promise((resolve) => {
    let busy = false;
    let touched = false;
    let serverError: UsernameServerError = null;
    let result: string | null | undefined = undefined;
    const input = h('input', {
      id: 'username-field',
      class: 'nick-field username-field',
      type: 'text',
      name: 'username',
      placeholder: t('username.placeholder'),
      autocomplete: 'off',
      autocapitalize: 'off',
      spellcheck: false,
      maxLength: USERNAME_MAX,
      enterKeyHint: 'done',
    });
    const hint = h('p', { class: 'username-hint', 'aria-live': 'polite' });
    const save = h('button', { type: 'submit', class: 'btn btn-go' }, t('username.save'));
    const later = deps.skippable
      ? h(
          'button',
          {
            type: 'button',
            class: 'btn btn-quiet',
            onClick: () => {
              deps.playClick?.();
              handle.close();
            },
          },
          t('username.later'),
        )
      : null;
    const render = (): void => {
      const v = usernameFormView({ value: input.value, touched, serverError, busy });
      hint.textContent = v.hint;
      hint.classList.toggle('is-error', v.hintIsError);
      save.textContent = v.saveLabel;
      save.setAttribute('aria-disabled', v.canSubmit ? 'false' : 'true');
      input.disabled = busy;
    };
    const submit = async (): Promise<void> => {
      const v = usernameFormView({ value: input.value, touched: true, serverError: null, busy });
      if (!v.canSubmit || busy) {
        touched = true;
        render();
        return;
      }
      deps.playClick?.();
      busy = true;
      serverError = null;
      render();
      const outcome = usernameSubmitOutcome(await deps.submit(input.value.trim()));
      busy = false;
      if (outcome.done) {
        result = outcome.name;
        handle.close();
        return;
      }
      serverError = outcome.error;
      render();
      input.focus();
    };
    input.addEventListener('input', () => {
      touched = true;
      serverError = null;
      render();
    });
    const titleId = 'username-title';
    const form = h(
      'form',
      {
        class: 'dialog dialog-username',
        novalidate: true,
        'aria-labelledby': titleId,
        onSubmit: (ev: Event) => {
          ev.preventDefault();
          void submit();
        },
      },
      h('h2', { class: 'dialog-title', id: titleId }, t('username.title')),
      h('p', { class: 'dialog-body' }, t('username.body')),
      h('label', { class: 'sr-only', htmlFor: 'username-field' }, t('username.label')),
      input,
      hint,
      h('div', { class: 'dialog-actions' }, later, save),
    );
    const handle: ModalHandle = openModal(form, {
      closable: true,
      label: t('username.title'),
      className: 'modal-message modal-username',
      onCloseClick: () => deps.playClick?.(),
      onClose: () => resolve(result),
    });
    render();
    input.focus();
  });
}
